// Which Gmail filter box catches only a bank's purchase alerts, from one real alert (the add-a-card
// flow's example). Chosen once, when the example is read (src/intake/pipeline.ts addExample()),
// because the body is never stored: only the result is (`bank_proposals.filter_kind` and
// `filter_value`). In order:
//   1. Subject, only when it is stable: the subject's fixed wording (amounts, numbers, the merchant
//      and the card's own words taken out, as `subjectPattern()` and `stableSubject()` do) is enough
//      to match on, has none of the alert's own fields (amount, merchant, date, card digits), and
//      says what kind of email it is (a word like "purchase").
//   2. Has the words: a short phrase (2 to 5 words) copied from the body. Checked here, never taken
//      from the model: it appears word for word in the alert, has no digits and none of the fields.
//      A phrase without the card's product name ("360 Checking") is preferred, so one filter fits
//      every card from that bank.
//   3. From only, with a warning that every email from the sender is forwarded.
// Pure: no I/O. Tested in test/filter-choice.test.ts.
import { stableSubject } from './card-flow';

export interface FilterSample {
  /** The alert's subject as the bank sent it ("Fwd:" is ignored). */
  subject: string;
  /** Its fixed wording as a Gmail subject query (src/intake/subject-pattern.ts subjectPattern()). */
  subjectPattern: string;
  /** The subject had too little fixed wording (subjectPattern() fell back to general words). */
  generalSubject: boolean;
  /** The alert's plain text body. In memory only. */
  body: string;
  merchant: string | null;
  amountCents: number;
  /** YYYY-MM-DD, when known. */
  date: string | null;
  last4: string | null;
}

export type FilterChoice =
  | { kind: 'subject'; subject: string }
  | {
      kind: 'words';
      /** The phrase, without quotes. */
      words: string;
      /** Why the subject could not be used (the screen says so). */
      subjectWhy: SubjectWhy;
    }
  | { kind: 'from' };

/**
 * Why a subject cannot be the filter: it has the amount or the store ('purchase'), another field
 * that changes (a date or the card digits: 'changes'), or it is the same on the bank's other email
 * too ('general': nothing in it says purchase).
 */
export type SubjectWhy = 'purchase' | 'changes' | 'general';

export function subjectWhy(sample: FilterSample): SubjectWhy {
  const f = hasAlertField(withoutForward(sample.subject), sample);
  return f.amount || f.merchant ? 'purchase' : f.date || f.digits ? 'changes' : 'general';
}

export const PHRASE_MIN_WORDS = 2;
export const PHRASE_MAX_WORDS = 5;
/** The phrase length tried first: long enough to be specific, short enough to survive small rewordings. */
const PHRASE_BEST_WORDS = 3;

/** Words that say an email is about a purchase. A subject or phrase needs one. */
const PURCHASE_WORDS = new Set([
  'transaction',
  'transactions',
  'purchase',
  'purchases',
  'charge',
  'charged',
  'spent',
  'spend',
  'authorized',
  'authorization',
  'approved',
]);

/**
 * Plain words every bank uses, none of them a product or card name. A phrase made only of these
 * (and a purchase word) is the same for every card at the bank. "Checking", "savings", "credit" and
 * "debit" are left out on purpose: they differ between cards at the same bank.
 */
const PLAIN_WORDS = new Set([
  ...PURCHASE_WORDS,
  'a',
  'an',
  'the',
  'your',
  'you',
  'we',
  'our',
  'this',
  'that',
  'these',
  'here',
  'is',
  'was',
  'were',
  'has',
  'have',
  'had',
  'been',
  'be',
  'made',
  'make',
  'using',
  'used',
  'use',
  'with',
  'on',
  'at',
  'for',
  'of',
  'to',
  'from',
  'by',
  'in',
  'as',
  'following',
  'recent',
  'new',
  'card',
  'account',
  'notice',
  'notification',
  'alert',
  'alerts',
  'details',
  'activity',
  'just',
  'large',
]);

/** Month names and abbreviations ("may" only next to a number: it is also a plain word). */
/** Filler words a phrase never starts or ends with ("A purchase was" reads as half a sentence). */
const FILLER = new Set(['a', 'an', 'the', 'your', 'you', 'we', 'our', 'this', 'that', 'these', 'is', 'was', 'were', 'has', 'have', 'had', 'been', 'be', 'with', 'on', 'at', 'for', 'of', 'to', 'from', 'by', 'in', 'as', 'and', 'or', 'but', 'if', 'because', 'so', 'when']);

const MONTHS = /\b(jan(uary)?|feb(ruary)?|mar(ch)?|apr(il)?|june?|july?|aug(ust)?|sept?(ember)?|oct(ober)?|nov(ember)?|dec(ember)?)\b|\bmay\s+\d|\d\s+may\b/i;
const NOT_MERCHANT = new Set(['com', 'www', 'net', 'org', 'inc', 'llc', 'ltd', 'the', 'and']);

const norm = (s: string) => s.replace(/\s+/g, ' ').trim();
const lower = (s: string) => norm(s).toLowerCase();

/** The merchant's own words (3 letters or more), lowercased: "APPLE.COM/BILL" -> apple, bill. */
export function merchantWords(merchant: string | null): string[] {
  if (!merchant) return [];
  return [...new Set(merchant.toLowerCase().split(/[^a-z]+/).filter((w) => w.length >= 3 && !NOT_MERCHANT.has(w)))];
}

/** The amount as it may be written: 2.99, 1,234.56, and $12 for whole dollars. */
function amountForms(cents: number): RegExp[] {
  const fixed = (Math.abs(cents) / 100).toFixed(2);
  const [whole, frac] = fixed.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const forms = [new RegExp(`(^|[^\\d])${grouped.replace(/,/g, ',?')}\\.${frac}(?!\\d)`)];
  if (frac === '00') forms.push(new RegExp(`\\$\\s?${grouped.replace(/,/g, ',?')}(?![\\d.,])`));
  return forms;
}

/** Does this text carry one of the alert's own fields (amount, merchant, date, card digits)? */
export function hasAlertField(text: string, s: Pick<FilterSample, 'merchant' | 'amountCents' | 'date' | 'last4'>): { amount: boolean; merchant: boolean; date: boolean; digits: boolean } {
  const t = lower(text);
  const merchant = !!s.merchant && (t.includes(lower(s.merchant)) || merchantWords(s.merchant).some((w) => new RegExp(`\\b${w}\\b`).test(t)));
  const amount = amountForms(s.amountCents).some((re) => re.test(t));
  const date = MONTHS.test(t) || /\b\d{4}-\d{2}-\d{2}\b/.test(t) || /\b\d{1,2}[/.-]\d{1,2}([/.-]\d{2,4})?\b/.test(t);
  const digits = !!s.last4 && t.includes(s.last4);
  return { amount, merchant, date, digits };
}

const anyField = (f: ReturnType<typeof hasAlertField>) => f.amount || f.merchant || f.date || f.digits;

const withoutForward = (subject: string) => subject.replace(/^\s*((fwd?|fw|re)\s*:\s*)+/i, '');

/**
 * The Subject box, or null when the subject cannot be used: its fixed wording (card-specific words
 * dropped) must have two words or more, none of the alert's fields and a purchase word.
 */
export function stableSubjectOf(sample: FilterSample): string | null {
  const { subject } = stableSubject({ sender: '', subjectPattern: sample.subjectPattern, generalSubject: sample.generalSubject, last4: sample.last4 }, null);
  if (!subject) return null;
  const words = subject.replace(/["()]/g, ' ').split(/\s+/).filter((w) => /[a-z]/i.test(w));
  if (words.length < 2) return null;
  if (anyField(hasAlertField(subject.replace(/"/g, ' '), sample))) return null;
  if (!words.some((w) => PURCHASE_WORDS.has(w.toLowerCase().replace(/[^a-z]/g, '')))) return null;
  return subject;
}

/**
 * Is this phrase safe for Gmail's "Has the words" box? Checked in code, whatever proposed it:
 * 2 to 5 words of letters only, word for word in the alert, no digits, and none of the alert's
 * fields (amount, merchant, date, card digits), and a word that says it is about a purchase.
 */
export function phraseOk(phrase: string, sample: FilterSample): boolean {
  const p = norm(phrase);
  const words = p.split(' ');
  if (words.length < PHRASE_MIN_WORDS || words.length > PHRASE_MAX_WORDS) return false;
  if (!words.every((w) => /^[A-Za-z]+$/.test(w))) return false;
  if (/\d/.test(p)) return false;
  if (!lower(sample.body).includes(p.toLowerCase())) return false;
  if (!words.some((w) => PURCHASE_WORDS.has(w.toLowerCase()))) return false;
  return !anyField(hasAlertField(p, sample));
}

/** Runs of plain-letter words in the body, in order: punctuation, digits and line breaks end a run. */
function runs(body: string): string[][] {
  const out: string[][] = [];
  for (const piece of body.split(/[\n\r.!?:;,|•·()[\]"<>/\\]+/)) {
    let run: string[] = [];
    for (const token of piece.split(/\s+/)) {
      if (/^[A-Za-z]+$/.test(token)) run.push(token);
      else {
        if (run.length) out.push(run);
        run = [];
      }
    }
    if (run.length) out.push(run);
  }
  return out;
}

/**
 * The best "Has the words" phrase in the body, or null. It starts and ends on a real word (never
 * "a", "the", "was" and the like). Plain-word phrases come first (they fit
 * every card at the bank), then any safe phrase; earlier in the alert first (the heading), and
 * three words before two, four or five.
 */
export function bodyPhrase(sample: FilterSample): string | null {
  type Candidate = { phrase: string; plain: boolean; at: number; len: number };
  const found: Candidate[] = [];
  runs(sample.body).forEach((run, at) => {
    for (let len = PHRASE_MIN_WORDS; len <= PHRASE_MAX_WORDS; len++)
      for (let i = 0; i + len <= run.length; i++) {
        const words = run.slice(i, i + len);
        if (FILLER.has(words[0].toLowerCase()) || FILLER.has(words[len - 1].toLowerCase())) continue;
        const phrase = words.join(' ');
        if (!phraseOk(phrase, sample)) continue;
        found.push({ phrase, plain: words.every((w) => PLAIN_WORDS.has(w.toLowerCase())), at, len });
      }
  });
  found.sort((a, b) => Number(b.plain) - Number(a.plain) || a.at - b.at || Math.abs(a.len - PHRASE_BEST_WORDS) - Math.abs(b.len - PHRASE_BEST_WORDS) || b.len - a.len);
  return found[0]?.phrase ?? null;
}

/** The filter for one alert, in the order above. */
export function chooseFilter(sample: FilterSample): FilterChoice {
  const subject = stableSubjectOf(sample);
  if (subject) return { kind: 'subject', subject };
  const words = bodyPhrase(sample);
  if (words) return { kind: 'words', words, subjectWhy: subjectWhy(sample) };
  return { kind: 'from' };
}

/** What Gmail's "Has the words" box gets: the phrase in quotes, so Gmail matches it as one phrase. */
export const quotedPhrase = (words: string) => `"${words}"`;
